/**
 * Simplified screenshot service that uses a direct canvas approach for all viewer types
 */
import directScreenshotService from "./DirectScreenshotService";
import { saveScreenshotToReport } from "./ScreenshotReportUtils";

class IntegratedScreenshotService {
  /**
   * Takes a screenshot of the given container using a direct canvas approach
   * @param {HTMLElement} containerElement - The container element
   * @param {Object} options - Options for screenshot capture
   * @param {boolean} options.debug - Enable debug output
   * @param {boolean} options.showNotification - Whether to show a notification
   * @param {boolean} options.persistToReport - Whether to store the blob in the report manager
   * @param {string} options.reportSection - Report section key for persistence
   * @param {Object} options.reportMetadata - Metadata passed alongside the stored record
   * @returns {Promise<{success: boolean, status?: string, fileName?: string, blob?: Blob, reportRecord?: Object, reason?: string, error?: Error}>}
   */
  async captureScreenshot(containerElement, options = {}) {
    const {
      debug = false,
      showNotification = false,
      persistToReport = true,
      reportSection,
      reportMetadata = {},
    } = options;

    try {
      if (!containerElement) {
        return { success: false, status: "failed", reason: "no-container" };
      }

      if (debug) {
      }

      if (!this.isClipboardSupported() && debug) {
        console.warn(
          "Clipboard API write not fully supported; attempting fallback"
        );
      }

      // Always use the direct capture method (handles fallbacks internally)
      const result = await directScreenshotService.captureToClipboard(
        containerElement,
        debug
      );
      const success =
        result?.status === "copied" || result?.status === "downloaded";
      let reportRecord = null;

      if (success && persistToReport && result?.blob instanceof Blob) {
        try {
          reportRecord = await saveScreenshotToReport(
            reportSection,
            result.blob,
            {
              ...reportMetadata,
              status: result.status,
              fileName: reportMetadata?.fileName || result.fileName,
            }
          );
        } catch (storageError) {}
      }

      // Show notification if requested
      if (showNotification) {
        if (result?.status === "copied") {
          this.showMessage("Screenshot copied to clipboard", "success");
        } else if (result?.status === "downloaded") {
          this.showMessage("Screenshot downloaded", "success");
        } else {
          let message = "Failed to capture screenshot";
          if (result?.reason === "invalid-dimensions") {
            message = "Viewer has no visible content to capture";
          } else if (result?.reason === "no-container") {
            message = "Viewer is not ready yet";
          }
          this.showMessage(message, "error");
        }
      }

      return {
        success,
        status: result?.status,
        reason: result?.reason,
        fileName: result?.fileName,
        blob: result?.blob,
        reportRecord,
        annexRecord: reportRecord,
      };
    } catch (error) {
      if (showNotification) {
        this.showMessage(`Error: ${error.message}`, "error");
      }
      return {
        success: false,
        status: "failed",
        reason: error?.message || "exception",
        error,
        reportRecord: null,
        annexRecord: null,
      };
    }
  }

  /**
   * Check if the Clipboard API is supported
   * @returns {boolean} - Whether the Clipboard API is supported
   */
  isClipboardSupported() {
    return (
      typeof navigator !== "undefined" &&
      navigator.clipboard !== undefined &&
      typeof navigator.clipboard.write === "function"
    );
  }

  /**
   * Show a message to the user
   * @param {string} message - The message to show
   * @param {string} type - The type of message (success, error, info)
   */
  showMessage(message, type = "info") {
    directScreenshotService.showNotification(message, type);
  }
}

// Create singleton instance
const simpleIntegratedScreenshotService = new IntegratedScreenshotService();
export default simpleIntegratedScreenshotService;
